// Client side of server-authoritative sync (spec §6.1), free of DOM and
// network so it can be tested directly.
//
// `confirmed` is the server's state as of `lastSeq`; `pending` holds local ops
// not yet echoed back by the server. The store always shows confirmed state
// with pending ops replayed on top ("rebase"). So a server value for something
// we have a pending op on stays invisible until our own op is acked (no
// flicker), a rejected op simply disappears from the replay (rollback), and a
// concurrent delete can't be resurrected by our pending edits.
import type { BoardObject, Op, ServerOp } from '../../shared/types.ts';
import { applyOpToMap, applyUpdate, opObjectId, toWire } from '../../shared/ops.ts';
import { applyItemOp } from '../../shared/cards.ts';
import { newId } from '../../shared/ids.ts';
import type { Store } from '../model/store.ts';

export interface OutgoingOp { clientOpId: string; op: Op }

export class SyncState {
  readonly confirmed = new Map<string, BoardObject>();
  /** Last server seq applied to `confirmed`; -1 before the first init. */
  lastSeq = -1;

  private readonly store: Store;
  private readonly userId: string;
  private pending: OutgoingOp[] = [];             // in local application order
  private readonly byObject = new Map<string, OutgoingOp[]>();
  private outbox: OutgoingOp[] = [];              // tail of `pending` not yet sent

  constructor(store: Store, userId: string) {
    this.store = store;
    this.userId = userId;
  }

  get pendingCount(): number { return this.pending.length; }
  /** Whether local ops on this object are still waiting for the server. */
  hasPending(id: string): boolean { return this.byObject.has(id); }
  get hasOutbox(): boolean { return this.outbox.length > 0; }

  /** Records ops the user just applied to the store. */
  local(ops: readonly Op[]): void {
    for (const raw of ops) {
      const op = toWire(raw);
      const id = opObjectId(op);
      // Fold into an unsent update of the same object: a drag sends one op per
      // object per batch instead of one per pointer event.
      const last = this.byObject.get(id)?.at(-1);
      const unsent = !!last && this.outbox.includes(last);
      if (unsent && op.t === 'update' && last.op.t === 'update') {
        last.op = { t: 'update', id, props: { ...last.op.props, ...op.props } };
        continue;
      }
      if (unsent && op.t === 'item' && op.props && last.op.t === 'item' && last.op.itemId === op.itemId && last.op.props) {
        last.op = { t: 'item', id, itemId: op.itemId, props: { ...last.op.props, ...op.props } };
        continue;
      }
      const entry: OutgoingOp = { clientOpId: newId(), op };
      this.pending.push(entry);
      this.outbox.push(entry);
      let list = this.byObject.get(id);
      if (!list) this.byObject.set(id, (list = []));
      list.push(entry);
    }
  }

  /** Ops to send now. */
  takeOutbox(): OutgoingOp[] {
    const out = this.outbox;
    this.outbox = [];
    return out;
  }

  /** After (re)joining, every unacked op is sent again; the server dedupes. */
  resendAll(): OutgoingOp[] {
    this.outbox = [];
    return [...this.pending];
  }

  /** Full state from the server; pending local ops are replayed on top. */
  init(snapshot: readonly BoardObject[], ops: readonly ServerOp[], seq: number): void {
    this.confirmed.clear();
    for (const o of snapshot) this.confirmed.set(o.id, o);
    this.lastSeq = -1;
    for (const sop of ops) this.applyServerOp(sop, new Set());
    this.lastSeq = seq;
    const ids = new Set([...this.confirmed.keys(), ...this.byObject.keys(), ...[...this.store.values()].map((o) => o.id)]);
    this.display(ids);
  }

  /** Server ops, broadcast or on resume. Ops already applied are only used as acks. */
  receive(ops: readonly ServerOp[]): void {
    const affected = new Set<string>();
    for (const sop of ops) this.applyServerOp(sop, affected);
    this.display(affected);
  }

  /** The server refused these ops: drop them, which rolls them back. */
  reject(clientOpIds: readonly string[]): void {
    const affected = new Set<string>();
    for (const id of clientOpIds) this.ack(id, affected);
    this.display(affected);
  }

  private applyServerOp(sop: ServerOp, affected: Set<string>): void {
    if (sop.seq > this.lastSeq) {
      applyOpToMap(this.confirmed, sop.op);
      affected.add(opObjectId(sop.op));
      this.lastSeq = sop.seq;
    }
    if (sop.userId === this.userId) this.ack(sop.clientOpId, affected);
  }

  private ack(clientOpId: string, affected: Set<string>): void {
    // Acks nearly always arrive in order, so this is usually the head.
    const i = this.pending[0]?.clientOpId === clientOpId ? 0 : this.pending.findIndex((p) => p.clientOpId === clientOpId);
    if (i < 0) return;
    const [entry] = this.pending.splice(i, 1);
    const id = opObjectId(entry!.op);
    const list = this.byObject.get(id)!;
    list.splice(list.indexOf(entry!), 1);
    if (!list.length) this.byObject.delete(id);
    const o = this.outbox.indexOf(entry!);
    if (o >= 0) this.outbox.splice(o, 1);
    affected.add(id);
  }

  /** Server state for `id` with our pending ops replayed on top. */
  private rebase(id: string): BoardObject | undefined {
    let obj = this.confirmed.get(id);
    for (const { op } of this.byObject.get(id) ?? []) {
      if (op.t === 'create') obj ??= op.obj;
      else if (op.t === 'update') { if (obj) obj = applyUpdate(obj, op.props); }
      // Per item, per field: a remote tick on another item stays visible (spec §13.2).
      else if (op.t === 'item') { if (obj?.type === 'card') obj = applyItemOp(obj, op.itemId, op.props); }
      else obj = undefined;
    }
    return obj;
  }

  private display(ids: Iterable<string>): void {
    const entries: [string, BoardObject | undefined][] = [];
    for (const id of ids) {
      const next = this.rebase(id);
      const cur = this.store.get(id);
      // Skip no-op writes so an echo of our own op doesn't trigger a redraw.
      if (cur === next || (cur && next && sameObject(cur, next))) continue;
      entries.push([id, next]);
    }
    if (entries.length) this.store.setMany(entries);
  }
}

function sameObject(a: BoardObject, b: BoardObject): boolean {
  const ka = Object.keys(a), kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  const ra = a as unknown as Record<string, unknown>, rb = b as unknown as Record<string, unknown>;
  for (const k of ka) {
    const va = ra[k], vb = rb[k];
    if (va === vb) continue;
    if (typeof va !== 'object' || typeof vb !== 'object' || JSON.stringify(va) !== JSON.stringify(vb)) return false;
  }
  return true;
}
